/**
 * LE RÉSULTAT — lot 101, 18/09/2026.
 *
 * Maquette `maquette-analyse-resultat-v2.html`, cadrage « Analyse
 * financière : 5. le résultat » et « 8. les arbitrages ».
 *
 * Trois vues : ensemble (SIG comptable, depuis la balance des comptes),
 * par activité et par client (marge sur coûts directs, depuis Stafa). Les
 * objectifs sont saisis, jamais proposés. Chaque montant du tableau ouvre ce
 * qui le fait : les COMPTES de la balance — la balance ne porte pas les
 * pièces, et l'écran le dit.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  comparer,
  FEUILLES,
  feuillesDeLigne,
  LIGNES_SIG,
  valeurLigne,
  type Feuille,
  type LigneSig,
  type MargeDirecte,
  type MoisSig,
} from '@stafa/core';
import type { EchecApi, Resultat } from './api.js';
import {
  ajouterCorrespondance,
  enregistrerObjectif,
  lireBalanceDuMois,
  lireResultat,
  retirerCorrespondance,
} from './apiAnalyse.js';
import { dateFr, euros, eurosCourts } from './bfr.js';
import { CourbeAnalyse } from './CourbeAnalyse.js';
import { eurosEntiers, eurosSignes, heureDeParis } from './tresorerie.js';
import {
  clientsDuResultat,
  comptesNonRattaches,
  margesDesMois,
  moisCourt,
  moisLong,
  NOMS_INDICATEURS,
  objectifDe,
  pourcent,
  serieDe,
  serieObjectif,
  sigDesMois,
  type Indicateur,
  type ResultatLu,
  type Vue,
} from './sig.js';

export interface ActionsResultat {
  readonly lireBalance: (mois: string) => Promise<string | null>;
  readonly poserObjectif: (
    mois: string,
    vue: Vue,
    indicateur: Indicateur,
    valeur: string | null,
  ) => Promise<string | null>;
  readonly ajouter: (
    motif: string,
    genre: 'compte' | 'libelle',
    feuille: Feuille,
  ) => Promise<string | null>;
  readonly retirer: (id: string) => Promise<string | null>;
}

export function EcranResultatAnalyse({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [donnees, setDonnees] = useState<ResultatLu | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  async function charger() {
    const j = await jeton();
    if (j === null) return quandAccesPerdu();
    const r = await lireResultat(j);
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? messageRefus(r.cause));
    }
  }

  useEffect(() => {
    void charger();
  }, [jeton]);

  async function avecJeton<T>(
    faire: (j: string) => Promise<Resultat<T>>,
  ): Promise<string | null> {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return 'Session terminée.';
    }
    const r = await faire(j);
    if (r.etat !== 'ok') {
      if (r.cause === 'acces') quandAccesPerdu();
      return r.message ?? messageRefus(r.cause);
    }
    await charger();
    return null;
  }

  const actions: ActionsResultat = {
    lireBalance: async (mois) => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return 'Session terminée.';
      }
      const r = await lireBalanceDuMois(j, mois);
      if (r.etat !== 'ok') {
        if (r.cause === 'acces') quandAccesPerdu();
        return r.message ?? messageRefus(r.cause);
      }
      if (r.valeur.issue !== 'complete') {
        return r.valeur.message ?? `Lecture de ${mois} : ${r.valeur.issue}.`;
      }
      return null;
    },
    poserObjectif: (mois, vue, indicateur, valeur) =>
      avecJeton((j) =>
        enregistrerObjectif(j, {
          mois,
          vue: vue.sorte,
          ...(vue.sorte === 'ensemble' ? {} : { cible: vue.cle }),
          indicateur,
          valeur,
        }),
      ),
    ajouter: (motif, genre, feuille) =>
      avecJeton((j) => ajouterCorrespondance(j, motif, genre, feuille)),
    retirer: (id) => avecJeton((j) => retirerCorrespondance(j, id)),
  };

  return (
    <VueResultat
      donnees={donnees}
      refus={refus}
      sources={sources(() => void charger())}
      actions={actions}
      quandRelire={() => void charger()}
    />
  );
}

type Panneau = 'sources' | 'objectifs' | 'comptes' | null;

export function VueResultat({
  donnees,
  refus,
  sources,
  actions,
  quandRelire,
  vueInitiale,
  panneauInitial = null,
  detailInitial = null,
}: {
  readonly donnees: ResultatLu | null;
  readonly refus: string | null;
  readonly sources: ReactNode;
  readonly actions: ActionsResultat;
  readonly quandRelire: () => void;
  readonly vueInitiale?: Vue;
  readonly panneauInitial?: Panneau;
  readonly detailInitial?: { ligne: string; mois: string } | null;
}) {
  const [vue, setVue] = useState<Vue>(vueInitiale ?? { sorte: 'ensemble' });
  const [panneau, setPanneau] = useState<Panneau>(panneauInitial);
  const [detail, setDetail] = useState(detailInitial);
  const [lecture, setLecture] = useState<{
    fait: number;
    total: number;
    message: string | null;
  } | null>(null);
  const [observe, setObserve] = useState<string | null>(null);

  const sig = useMemo(() => (donnees === null ? null : sigDesMois(donnees)), [donnees]);
  const marges = useMemo(
    () =>
      donnees === null || vue.sorte === 'ensemble'
        ? null
        : margesDesMois(donnees, { sorte: vue.sorte, cle: vue.cle }),
    [donnees, vue],
  );

  if (donnees === null || sig === null) {
    return (
      <main className="corps corps-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture du résultat…</p>
        )}
      </main>
    );
  }

  const mois = donnees.mois;
  const dernier = mois[mois.length - 1] ?? donnees.aujourdhui.slice(0, 7);
  const moisObserve = observe ?? dernier;
  const rang = mois.indexOf(moisObserve);
  const clients = clientsDuResultat(donnees);
  const nonRattaches = comptesNonRattaches(sig.mois);

  async function lireToutesLesBalances() {
    setLecture({ fait: 0, total: mois.length, message: null });
    for (const [i, m] of mois.entries()) {
      const erreur = await actions.lireBalance(m);
      setLecture({ fait: i + 1, total: mois.length, message: erreur });
      if (erreur !== null) return;
    }
    setLecture(null);
    quandRelire();
  }

  const indicateurs: Indicateur[] =
    vue.sorte === 'ensemble' ? ['ca', 'ebe', 'rn'] : ['ca', 'mcd'];

  return (
    <main className="corps corps-analyse">
      <div className="bfr-filtres">
        <div className="ech-champ">
          Vue
          <div className="bfr-segments" role="group" aria-label="Vue">
            <button
              type="button"
              aria-pressed={vue.sorte === 'ensemble'}
              onClick={() => setVue({ sorte: 'ensemble' })}
            >
              Ensemble
            </button>
            <button
              type="button"
              aria-pressed={vue.sorte === 'activite'}
              onClick={() =>
                setVue({ sorte: 'activite', cle: donnees.activites[0]?.id ?? '' })
              }
              disabled={donnees.activites.length === 0}
            >
              Par activité
            </button>
            <button
              type="button"
              aria-pressed={vue.sorte === 'client'}
              onClick={() => setVue({ sorte: 'client', cle: clients[0]?.cle ?? '' })}
              disabled={clients.length === 0}
            >
              Par client
            </button>
          </div>
        </div>
        {vue.sorte !== 'ensemble' && (
          <label className="ech-champ">
            {vue.sorte === 'activite' ? 'Activité' : 'Client'}
            <select
              value={vue.cle}
              onChange={(e) => setVue({ sorte: vue.sorte, cle: e.target.value })}
            >
              {(vue.sorte === 'activite'
                ? donnees.activites.map((a) => ({ cle: a.id, nom: a.nom }))
                : clients
              ).map((x) => (
                <option key={x.cle} value={x.cle}>
                  {x.nom}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="ech-champ">
          Mois observé
          <select value={moisObserve} onChange={(e) => setObserve(e.target.value)}>
            {mois.map((m) => (
              <option key={m} value={m}>
                {moisLong(m)}
              </option>
            ))}
          </select>
        </label>
        <div className="bfr-outils">
          <button
            type="button"
            className="bouton bouton-petit"
            onClick={() => void lireToutesLesBalances()}
            disabled={lecture !== null}
          >
            {lecture === null
              ? 'Lire la balance (12 mois)'
              : `Lecture ${lecture.fait}/${lecture.total}…`}
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'objectifs'}
            onClick={() => setPanneau(panneau === 'objectifs' ? null : 'objectifs')}
          >
            Objectifs
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'comptes'}
            onClick={() => setPanneau(panneau === 'comptes' ? null : 'comptes')}
          >
            Rattachement des comptes
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'sources'}
            onClick={() => setPanneau(panneau === 'sources' ? null : 'sources')}
          >
            Données Pennylane
          </button>
        </div>
      </div>
      <p className="ech-sous">
        {vue.sorte === 'ensemble'
          ? 'Comptabilité Pennylane, balance des comptes. Le mois en cours est complété par les prévisions, marquées « estimé ».'
          : 'Stafa : chiffre d’affaires des ordres et coût de revient réel. La structure n’est pas répartie — la cascade s’arrête à la marge sur coûts directs.'}
        {donnees.derniere_lecture !== null &&
          ` · Balance lue le ${heureDeParis(donnees.derniere_lecture.lance_le)} par ${donnees.derniere_lecture.lance_par}.`}
        {donnees.derniere_lecture === null && ' · Balance jamais lue.'}
      </p>
      {lecture?.message != null && (
        <p className="bandeau bandeau-refus">{lecture.message}</p>
      )}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {panneau === 'sources' && <div className="ech-sources">{sources}</div>}
      {panneau === 'objectifs' && (
        <div className="ech-sources">
          <PanneauObjectifs
            donnees={donnees}
            vue={vue}
            indicateurs={indicateurs}
            actions={actions}
          />
        </div>
      )}
      {panneau === 'comptes' && (
        <div className="ech-sources">
          <PanneauComptes
            donnees={donnees}
            nonRattaches={nonRattaches}
            actions={actions}
          />
        </div>
      )}
      {vue.sorte === 'ensemble' && nonRattaches.length > 0 && panneau !== 'comptes' && (
        <p className="bandeau bandeau-attente">
          {nonRattaches.length} compte(s) ne sont rattachés à aucune ligne du SIG : ils
          sont comptés dans les soldes, sur la ligne « Comptes non rattachés ».{' '}
          <button
            type="button"
            className="treso-case"
            onClick={() => setPanneau('comptes')}
          >
            Les rattacher
          </button>
        </p>
      )}

      {vue.sorte === 'ensemble' ? (
        <VueEnsemble
          donnees={donnees}
          sig={sig}
          rang={rang}
          vue={vue}
          quandDetail={(ligne, m) => setDetail({ ligne, mois: m })}
          quandMois={(m) => setObserve(m)}
        />
      ) : (
        <VueDirecteEcran
          donnees={donnees}
          marges={marges ?? []}
          rang={rang}
          vue={vue}
          quandMois={(m) => setObserve(m)}
          quandDetail={(m) => setDetail({ ligne: 'mcd', mois: m })}
        />
      )}

      {detail !== null && (
        <FenetreDetail
          detail={detail}
          donnees={donnees}
          sig={sig}
          marges={marges}
          vue={vue}
          quandFermer={() => setDetail(null)}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// La vue d'ensemble
// ---------------------------------------------------------------------------

function Tuile({
  titre,
  c,
}: {
  readonly titre: string;
  readonly c: {
    valeur: number;
    objectif: number | null;
    precedent: number | null;
    moyenne3: number | null;
  };
}) {
  return (
    <section className="treso-tuile">
      <h3>{titre}</h3>
      <strong>{eurosEntiers(c.valeur)}</strong>
      <p>
        objectif {c.objectif === null ? '—' : eurosEntiers(c.objectif)}
        {c.objectif !== null && ` (${pourcent(c.valeur, c.objectif)})`}
      </p>
      <p>
        mois précédent {pourcent(c.valeur, c.precedent)} · moyenne 3 mois{' '}
        {pourcent(c.valeur, c.moyenne3)}
      </p>
    </section>
  );
}

function VueEnsemble({
  donnees,
  sig,
  rang,
  vue,
  quandDetail,
  quandMois,
}: {
  readonly donnees: ResultatLu;
  readonly sig: { mois: MoisSig[]; cumul: MoisSig };
  readonly rang: number;
  readonly vue: Vue;
  readonly quandDetail: (ligne: string, mois: string) => void;
  readonly quandMois: (mois: string) => void;
}) {
  const ligne = (cle: string) => LIGNES_SIG.find((l) => l.cle === cle) as LigneSig;
  const etiquettes = donnees.mois.map(moisCourt);
  const enCours = donnees.mois.indexOf(donnees.aujourdhui.slice(0, 7));
  return (
    <>
      <div className="treso-tuiles">
        {(['ca', 'ebe', 'rn'] as const).map((i) => (
          <Tuile
            key={i}
            titre={NOMS_INDICATEURS[i]}
            c={comparer(
              sig.mois,
              rang,
              ligne(i),
              objectifDe(donnees.objectifs, vue, donnees.mois[rang] ?? '', i),
            )}
          />
        ))}
      </div>
      <section className="bfr-section">
        <div className="bfr-rangee deux">
          <CourbeAnalyse
            titre="Chiffre d’affaires"
            sousTitre="12 mois glissants · objectif saisi"
            etiquettes={etiquettes}
            series={[
              { nom: 'CA', teinte: 1, valeurs: serieDe(sig.mois, ligne('ca')) },
              {
                nom: 'objectif',
                teinte: 3,
                valeurs: serieObjectif(donnees, vue, 'ca'),
                pointillee: true,
              },
            ]}
            courant={rang}
            futurDepuis={enCours === -1 ? donnees.mois.length : enCours}
            format={eurosCourts}
            quandClic={(i) => quandMois(donnees.mois[i] ?? '')}
          />
          <CourbeAnalyse
            titre="EBE et résultat net"
            sousTitre="le mois en cours est estimé"
            etiquettes={etiquettes}
            series={[
              { nom: 'EBE', teinte: 2, valeurs: serieDe(sig.mois, ligne('ebe')) },
              {
                nom: 'résultat net',
                teinte: 4,
                valeurs: serieDe(sig.mois, ligne('rn')),
              },
            ]}
            courant={rang}
            futurDepuis={enCours === -1 ? donnees.mois.length : enCours}
            format={eurosCourts}
            quandClic={(i) => quandMois(donnees.mois[i] ?? '')}
          />
        </div>
      </section>
      <section className="bfr-section">
        <h2 className="bfr-section-titre">Soldes intermédiaires de gestion</h2>
        <div className="treso-cadre">
          <table className="treso-tableau sig-tableau">
            <thead>
              <tr>
                <th scope="col">Ligne</th>
                {donnees.mois.map((m, i) => (
                  <th
                    key={m}
                    scope="col"
                    className={`nombre${i === rang ? ' sig-observe' : ''}`}
                  >
                    {moisCourt(m)}
                    {sig.mois[i]?.estime === true && <span>estimé</span>}
                  </th>
                ))}
                <th scope="col" className="nombre">
                  Cumul
                </th>
              </tr>
            </thead>
            <tbody>
              {LIGNES_SIG.map((l) => (
                <tr
                  key={l.cle}
                  className={`${l.de === undefined ? '' : 'treso-solde'} ${l.fort === true ? 'sig-fort' : ''} ${l.sous === true ? 'sig-sous' : ''}`}
                >
                  <th scope="row">{l.nom}</th>
                  {sig.mois.map((m, i) => {
                    const v = valeurLigne(l, m);
                    return (
                      <td
                        key={m.mois}
                        className={`nombre${i === rang ? ' sig-observe' : ''}`}
                      >
                        {v === 0 ? (
                          <span className="ech-doux">·</span>
                        ) : (
                          <button
                            type="button"
                            className="treso-case"
                            onClick={() => quandDetail(l.cle, m.mois)}
                          >
                            {eurosEntiers(v)}
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="nombre">{eurosEntiers(valeurLigne(l, sig.cumul))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Les vues par activité et par client
// ---------------------------------------------------------------------------

function VueDirecteEcran({
  donnees,
  marges,
  rang,
  vue,
  quandMois,
  quandDetail,
}: {
  readonly donnees: ResultatLu;
  readonly marges: readonly MargeDirecte[];
  readonly rang: number;
  readonly vue: Vue;
  readonly quandMois: (mois: string) => void;
  readonly quandDetail: (mois: string) => void;
}) {
  const m = marges[rang];
  const objectifCa = objectifDe(donnees.objectifs, vue, donnees.mois[rang] ?? '', 'ca');
  const objectifMcd = objectifDe(
    donnees.objectifs,
    vue,
    donnees.mois[rang] ?? '',
    'mcd',
  );
  const precedent = marges[rang - 1];
  const trois = marges.slice(Math.max(0, rang - 3), rang);
  const moyenne = (f: (x: MargeDirecte) => number | null) => {
    const v = trois.map(f).filter((x): x is number => x !== null);
    return v.length === 0 ? null : Math.round(v.reduce((a, b) => a + b, 0) / v.length);
  };
  return (
    <>
      <div className="treso-tuiles">
        <Tuile
          titre="Chiffre d’affaires"
          c={{
            valeur: m?.ca ?? 0,
            objectif: objectifCa,
            precedent: precedent?.ca ?? null,
            moyenne3: moyenne((x) => x.ca),
          }}
        />
        <Tuile
          titre="Marge sur coûts directs"
          c={{
            valeur: m?.marge ?? 0,
            objectif: objectifMcd,
            precedent: precedent?.marge ?? null,
            moyenne3: moyenne((x) => x.marge),
          }}
        />
        <section className="treso-tuile">
          <h3>Taux de marge</h3>
          <strong>
            {m?.taux == null
              ? '—'
              : `${(m.taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
          </strong>
          <p>
            {m?.non_disponible === true
              ? 'coût non disponible : Stafa ne chiffrait pas encore ces ordres'
              : `${String(m?.ordres.length ?? 0)} ordre(s), ${String(m?.charges.length ?? 0)} charge(s) rattachée(s)`}
          </p>
        </section>
      </div>
      <section className="bfr-section">
        <CourbeAnalyse
          titre="Chiffre d’affaires et marge sur coûts directs"
          sousTitre="douze mois glissants · coût de revient réel Stafa"
          etiquettes={donnees.mois.map(moisCourt)}
          series={[
            { nom: 'CA', teinte: 1, valeurs: marges.map((x) => x.ca) },
            { nom: 'marge', teinte: 3, valeurs: marges.map((x) => x.marge) },
            {
              nom: 'objectif de marge',
              teinte: 4,
              valeurs: serieObjectif(donnees, vue, 'mcd'),
              pointillee: true,
            },
          ]}
          courant={rang}
          futurDepuis={donnees.mois.length}
          format={eurosCourts}
          quandClic={(i) => quandMois(donnees.mois[i] ?? '')}
        />
      </section>
      <section className="bfr-section">
        <h2 className="bfr-section-titre">Mois par mois</h2>
        <div className="treso-cadre">
          <table className="treso-tableau">
            <thead>
              <tr>
                <th scope="col">Mois</th>
                <th scope="col" className="nombre">
                  Chiffre d’affaires
                </th>
                <th scope="col" className="nombre">
                  dont affrètement acheté
                </th>
                <th scope="col" className="nombre">
                  Coûts directs
                </th>
                <th scope="col" className="nombre">
                  Marge
                </th>
                <th scope="col" className="nombre">
                  Taux
                </th>
                <th scope="col" className="nombre">
                  Objectif
                </th>
              </tr>
            </thead>
            <tbody>
              {marges.map((x, i) => (
                <tr key={x.mois} className={i === rang ? 'sig-observe' : ''}>
                  <th scope="row">
                    <button
                      type="button"
                      className="treso-case"
                      onClick={() => quandDetail(x.mois)}
                    >
                      {moisLong(x.mois)}
                    </button>
                  </th>
                  <td className="nombre">{eurosEntiers(x.ca)}</td>
                  <td className="nombre">
                    {x.achats_affr === 0 ? '·' : eurosEntiers(x.achats_affr)}
                  </td>
                  <td className="nombre">
                    {x.non_disponible ? 'n.d.' : eurosEntiers(x.couts)}
                  </td>
                  <td className="nombre">
                    {x.marge === null ? 'n.d.' : eurosEntiers(x.marge)}
                  </td>
                  <td className="nombre">
                    {x.taux === null
                      ? '—'
                      : `${(x.taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
                  </td>
                  <td className="nombre">
                    {objectifDe(donnees.objectifs, vue, x.mois, 'mcd') === null
                      ? '—'
                      : eurosEntiers(
                          objectifDe(donnees.objectifs, vue, x.mois, 'mcd') as number,
                        )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Les panneaux
// ---------------------------------------------------------------------------

export function PanneauObjectifs({
  donnees,
  vue,
  indicateurs,
  actions,
}: {
  readonly donnees: ResultatLu;
  readonly vue: Vue;
  readonly indicateurs: readonly Indicateur[];
  readonly actions: ActionsResultat;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [tous, setTous] = useState('');
  const [indicateur, setIndicateur] = useState<Indicateur>(indicateurs[0] ?? 'ca');

  return (
    <section className="reglages-analyse" aria-label="Objectifs">
      <h3>
        Objectifs{' '}
        {vue.sorte === 'ensemble'
          ? 'de la vue d’ensemble'
          : `de la vue par ${vue.sorte}`}
      </h3>
      <p className="ech-sous">
        Rien n’est proposé : un objectif vide veut dire « pas d’objectif ». Les montants
        sont en euros.
      </p>
      <form
        className="treso-regle"
        onSubmit={(e) => {
          e.preventDefault();
          void (async () => {
            for (const m of donnees.mois) {
              const erreur = await actions.poserObjectif(
                m,
                vue,
                indicateur,
                tous === '' ? null : tous,
              );
              if (erreur !== null) return setMessage(erreur);
            }
            setMessage(null);
          })();
        }}
      >
        <label className="ech-champ">
          Remplir tous les mois
          <select
            value={indicateur}
            onChange={(e) => setIndicateur(e.target.value as Indicateur)}
          >
            {indicateurs.map((i) => (
              <option key={i} value={i}>
                {NOMS_INDICATEURS[i]}
              </option>
            ))}
          </select>
        </label>
        <label className="ech-champ">
          Montant (vide : efface)
          <input
            inputMode="decimal"
            value={tous}
            onChange={(e) => setTous(e.target.value)}
          />
        </label>
        <button type="submit" className="bouton bouton-petit">
          Appliquer aux 12 mois
        </button>
        {message !== null && <span className="ech-ko">{message}</span>}
      </form>
      <div className="treso-cadre">
        <table className="treso-tableau">
          <thead>
            <tr>
              <th scope="col">Mois</th>
              {indicateurs.map((i) => (
                <th key={i} scope="col" className="nombre">
                  {NOMS_INDICATEURS[i]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {donnees.mois.map((m) => (
              <tr key={m}>
                <th scope="row">{moisLong(m)}</th>
                {indicateurs.map((i) => (
                  <td key={i} className="nombre">
                    <ChampObjectif
                      valeur={objectifDe(donnees.objectifs, vue, m, i)}
                      quandPoser={(v) =>
                        actions.poserObjectif(m, vue, i, v).then(setMessage)
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ChampObjectif({
  valeur,
  quandPoser,
}: {
  readonly valeur: number | null;
  readonly quandPoser: (v: string | null) => Promise<void>;
}) {
  const ecrit = valeur === null ? '' : String(valeur / 100).replace('.', ',');
  const [saisie, setSaisie] = useState(ecrit);
  useEffect(() => setSaisie(ecrit), [ecrit]);
  return (
    <input
      className="sig-objectif"
      inputMode="decimal"
      value={saisie}
      aria-label="objectif"
      onChange={(e) => setSaisie(e.target.value)}
      onBlur={() => {
        if (saisie === ecrit) return;
        void quandPoser(saisie.trim() === '' ? null : saisie.trim());
      }}
    />
  );
}

export function PanneauComptes({
  donnees,
  nonRattaches,
  actions,
}: {
  readonly donnees: ResultatLu;
  readonly nonRattaches: readonly {
    numero: string;
    libelle: string;
    montant: number;
  }[];
  readonly actions: ActionsResultat;
}) {
  const [motif, setMotif] = useState('');
  const [genre, setGenre] = useState<'compte' | 'libelle'>('compte');
  const [feuille, setFeuille] = useState<Feuille>('autres_ext');
  const [message, setMessage] = useState<string | null>(null);

  return (
    <section className="reglages-analyse" aria-label="Rattachement des comptes">
      <h3>Rattachement des comptes et des prévisions</h3>
      <p className="ech-sous">
        Un compte est rattaché par le DÉBUT de son numéro (« 6132 »), une prévision par
        un mot de son libellé. Vos règles passent avant les règles de base ; le préfixe
        le plus long l’emporte.
      </p>
      <form
        className="treso-regle"
        onSubmit={(e) => {
          e.preventDefault();
          void actions.ajouter(motif, genre, feuille).then((m) => {
            setMessage(m);
            if (m === null) setMotif('');
          });
        }}
      >
        <label className="ech-champ">
          Genre
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value as 'compte' | 'libelle')}
          >
            <option value="compte">Début de numéro de compte</option>
            <option value="libelle">Mot d’un libellé de prévision</option>
          </select>
        </label>
        <label className="ech-champ">
          {genre === 'compte' ? 'Numéro' : 'Mot'}
          <input
            value={motif}
            maxLength={40}
            onChange={(e) => setMotif(e.target.value)}
          />
        </label>
        <label className="ech-champ">
          Ligne du SIG
          <select
            value={feuille}
            onChange={(e) => setFeuille(e.target.value as Feuille)}
          >
            {FEUILLES.filter((f) => f.cle !== 'non_rattache').map((f) => (
              <option key={f.cle} value={f.cle}>
                {f.nom}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          className="bouton bouton-petit"
          disabled={motif.trim().length < 2}
        >
          Rattacher
        </button>
        {message !== null && <span className="ech-ko">{message}</span>}
      </form>
      {nonRattaches.length > 0 && (
        <>
          <h4 className="sig-titre">Comptes non rattachés sur les douze mois</h4>
          <ul className="ech-liste">
            {nonRattaches.map((c) => (
              <li key={c.numero}>
                <span>{c.libelle}</span>
                <span className="nombre">{eurosSignes(c.montant)}</span>
                <button
                  type="button"
                  className="bouton bouton-discret bouton-petit prev-a-droite"
                  onClick={() => {
                    setGenre('compte');
                    setMotif(c.numero);
                  }}
                >
                  Rattacher celui-ci
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <h4 className="sig-titre">Vos règles ({donnees.correspondances.length})</h4>
      {donnees.correspondances.length === 0 ? (
        <p className="ech-vide">
          Aucune règle à vous : seules les règles de base s’appliquent.
        </p>
      ) : (
        <ul className="ech-liste">
          {donnees.correspondances.map((c) => (
            <li key={c.id}>
              <strong>{c.motif}</strong>
              <span>
                ({c.genre === 'compte' ? 'compte' : 'libellé'}) →{' '}
                {FEUILLES.find((f) => f.cle === c.feuille)?.nom ?? c.feuille}
              </span>
              <button
                type="button"
                className="bouton bouton-discret bouton-petit prev-a-droite"
                onClick={() => void actions.retirer(c.id).then(setMessage)}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// La fenêtre de détail
// ---------------------------------------------------------------------------

function FenetreDetail({
  detail,
  donnees,
  sig,
  marges,
  vue,
  quandFermer,
}: {
  readonly detail: { ligne: string; mois: string };
  readonly donnees: ResultatLu;
  readonly sig: { mois: MoisSig[]; cumul: MoisSig };
  readonly marges: readonly MargeDirecte[] | null;
  readonly vue: Vue;
  readonly quandFermer: () => void;
}) {
  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [quandFermer]);

  const m = sig.mois.find((x) => x.mois === detail.mois);
  const ligne = LIGNES_SIG.find((l) => l.cle === detail.ligne);
  const marge = marges?.find((x) => x.mois === detail.mois);

  let titre = '';
  let corps: ReactNode = null;
  if (vue.sorte !== 'ensemble' && marge !== undefined) {
    titre = `Marge sur coûts directs · ${moisLong(detail.mois)}`;
    corps = (
      <>
        <p className="bfr-calcul">
          {eurosEntiers(marge.ca)} de CA −{' '}
          {marge.non_disponible
            ? 'coût non disponible'
            : `${eurosEntiers(marge.couts)} de coûts directs`}{' '}
          = <strong>{marge.marge === null ? 'n.d.' : eurosEntiers(marge.marge)}</strong>
        </p>
        <details className="bfr-bloc" open>
          <summary>
            <span>
              Ordres de transport{' '}
              <span className="ech-doux">({marge.ordres.length})</span>
            </span>
            <strong>{euros(marge.ca)}</strong>
          </summary>
          <div className="ech-tableau">
            <table>
              <thead>
                <tr>
                  <th>Ordre</th>
                  <th>Client</th>
                  <th className="nombre">Vente HT</th>
                  <th className="nombre">Coût réel</th>
                </tr>
              </thead>
              <tbody>
                {marge.ordres.map((o) => (
                  <tr key={o.id}>
                    <td>{o.numero}</td>
                    <td>{o.client_nom}</td>
                    <td className="nombre">{euros(o.vente_ht)}</td>
                    <td className="nombre">
                      {o.cout_reel === null ? 'n.d.' : euros(o.cout_reel)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
        {marge.charges.length > 0 && (
          <details className="bfr-bloc" open>
            <summary>
              <span>
                Charges rattachées à l’activité{' '}
                <span className="ech-doux">({marge.charges.length})</span>
              </span>
              <strong>{euros(marge.charges.reduce((a, c) => a + c.ht, 0))}</strong>
            </summary>
            <ul className="ech-liste">
              {marge.charges.map((c) => (
                <li key={c.cle}>
                  <span>{c.libelle}</span>
                  <span className="nombre">{euros(c.ht)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </>
    );
  } else if (m !== undefined && ligne !== undefined) {
    const feuilles = feuillesDeLigne(ligne);
    titre = `${ligne.nom} · ${moisLong(detail.mois)}`;
    corps = (
      <>
        <p className="bfr-calcul">
          {feuilles
            .map(
              (f) =>
                `${FEUILLES.find((x) => x.cle === f)?.nom ?? f} ${eurosSignes(m.valeurs[f])}`,
            )
            .join(' · ')}{' '}
          = <strong>{eurosEntiers(valeurLigne(ligne, m))}</strong>
        </p>
        <p className="ech-petit ech-doux">
          La balance des comptes donne des totaux par compte : le détail montre les
          COMPTES, pas les factures.
        </p>
        {feuilles.map((f) => (
          <details key={f} className="bfr-bloc" open={feuilles.length <= 3}>
            <summary>
              <span>
                {FEUILLES.find((x) => x.cle === f)?.nom ?? f}{' '}
                <span className="ech-doux">({m.apports[f].length})</span>
              </span>
              <strong>{eurosSignes(m.valeurs[f], true)}</strong>
            </summary>
            {m.apports[f].length === 0 ? (
              <p className="ech-vide">Aucun compte.</p>
            ) : (
              <ul className="ech-liste">
                {m.apports[f].map((a) => (
                  <li key={a.cle}>
                    <span>
                      {a.libelle}
                      {a.estime && <span className="ech-etiquette">estimé</span>}
                    </span>
                    <span className="ech-petit ech-doux">{a.note}</span>
                    <span className="nombre">{eurosSignes(a.montant, true)}</span>
                  </li>
                ))}
              </ul>
            )}
          </details>
        ))}
      </>
    );
  }

  return (
    <div className="ech-voile bfr-voile" role="presentation" onClick={quandFermer}>
      <div
        className="bfr-fenetre"
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>{titre}</h2>
            <p className="ech-sous">
              {vue.sorte === 'ensemble'
                ? `Comptabilité au ${dateFr(donnees.aujourdhui)}`
                : 'Chiffres Stafa'}
            </p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>
        {corps}
      </div>
    </div>
  );
}
